import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Alert, Button, EmptyState, Spinner } from '@opendate-app/setlist';

import WebOrderFormApp from '../../WebOrderForm/startup/WebOrderFormApp';
import MonthGrid from './MonthGrid';
import { useEntrySlots } from '../hooks/useEntrySlots';
import { parseLocalDate } from '../utils/parseLocalDate';

const axios = require('axios').default;

const STATE_LABELS = {
  available: 'Available',
  almost_gone: 'Almost Gone',
  sold_out: 'Sold Out'
};

const ATTRIBUTION_COMMANDS = ['setClientID', 'setCustomerioId', 'setParentUrl', 'setOriginAttribution', 'setMetaClickId', 'setAttributionVisitor'];

const slotKey = (slot) => (slot ? `${slot.confirm_id}-${slot.ticket_group_id || 'all'}` : null);

const longDate = (date) => parseLocalDate(date)?.toLocaleDateString(undefined, {
  weekday: 'long', month: 'long', day: 'numeric'
});

const Step = ({ number, title, aside, active, narrow, children }) => (
  <section className={`sep-step ${narrow ? 'sep-step--narrow' : ''}`}>
    <header className="sep-step__head">
      <span className={`sep-step__num ${active ? 'is-active' : ''}`} aria-hidden="true">{number}</span>
      <h2 className={`sep-step__title ${active ? 'is-active' : ''}`}>{title}</h2>
      {aside ? <span className="sep-step__aside">{aside}</span> : null}
    </header>
    {children}
  </section>
);

Step.propTypes = {
  number: PropTypes.number.isRequired,
  title: PropTypes.string.isRequired,
  aside: PropTypes.string,
  active: PropTypes.bool,
  narrow: PropTypes.bool,
  children: PropTypes.node
};

const SeriesEntryPicker = ({ dates, selectedDate: initialDate, entrySlotsUrl, defaultMonth, minDate, maxDate, realTimeEnabled }) => {
  const [selectedDate, setSelectedDate] = useState(initialDate || null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [checkout, setCheckout] = useState({ props: null, loading: false, failed: false });
  const { slots, loading, failed, selectDate, retry } = useEntrySlots({ entrySlotsUrl, realTimeEnabled });

  const latestCheckoutRequest = useRef(0);

  const held = useRef(null);
  const placingOrder = useRef(false);
  const attribution = useRef({});

  useEffect(() => {
    const remember = (event) => {
      const payload = event.data;
      if (payload && payload.command === 'setAttributionVisitor' && event.source !== window.parent) return;
      if (payload && payload.from === 'od_embed.js' && ATTRIBUTION_COMMANDS.includes(payload.command)) {
        attribution.current[payload.command] = payload;
      }
    };

    window.addEventListener('message', remember, false);
    return () => window.removeEventListener('message', remember, false);
  }, []);

  const releaseHeldReservation = useCallback(() => {
    const holding = held.current;
    held.current = null;
    if (!holding?.token) return;

    axios.delete(`/confirms/${holding.confirmId}/ticket_reservations/${holding.token}`, {
      headers: { 'X-CSRF-TOKEN': holding.csrfToken }
    }).catch(() => {});
  }, []);

  const loadCheckout = useCallback((slot) => {
    const request = ++latestCheckoutRequest.current;
    setCheckout({ props: null, loading: true, failed: false });

    return axios
      .get(slot.widget_url, { params: { format: 'json' } })
      .then(({ data }) => {
        if (latestCheckoutRequest.current !== request) return;
        setCheckout({ props: data, loading: false, failed: false });
      })
      .catch(() => {
        if (latestCheckoutRequest.current !== request) return;
        setCheckout({ props: null, loading: false, failed: true });
      });
  }, []);

  const [restartNonce, setRestartNonce] = useState(0);

  const restartCheckout = useCallback(() => {
    if (!selectedSlot || placingOrder.current) return;

    releaseHeldReservation();
    setRestartNonce((n) => n + 1);
    loadCheckout(selectedSlot);
  }, [selectedSlot, loadCheckout, releaseHeldReservation]);

  const chooseSlot = useCallback((slot) => {
    if (placingOrder.current) return;

    releaseHeldReservation();
    setSelectedSlot(slot);
    loadCheckout(slot);
  }, [loadCheckout, releaseHeldReservation]);

  const handleDateChange = (date) => {
    if (date === selectedDate || placingOrder.current) return;

    releaseHeldReservation();
    setSelectedDate(date);
    setSelectedSlot(null);
    setCheckout({ props: null, loading: false, failed: false });
    latestCheckoutRequest.current += 1;

    selectDate(date).then((fetched) => {
      if (fetched.length === 1 && fetched[0].state !== 'sold_out') chooseSlot(fetched[0]);
    });
  };

  const restored = useRef(false);
  useEffect(() => {
    if (restored.current || !initialDate) return;
    restored.current = true;
    selectDate(initialDate).then((fetched) => {
      if (fetched.length === 1 && fetched[0].state !== 'sold_out') chooseSlot(fetched[0]);
    });
  }, [initialDate, selectDate, chooseSlot]);

  useEffect(() => {
    if (!checkout.props) return;

    Object.values(attribution.current).forEach((payload) => window.postMessage(payload, window.location.origin));
  }, [checkout.props, selectedSlot, restartNonce]);

  if (dates.length === 0) {
    return <EmptyState title="Tickets coming soon" description="No dates are on sale for this series yet." />;
  }

  const activeKey = slotKey(selectedSlot);
  const checkoutKey = `${activeKey}-${restartNonce}`;

  return (
    <div className="sep">
      <Step number={1} title="Select a Date" active narrow>
        <MonthGrid
          dates={dates}
          value={selectedDate}
          onChange={handleDateChange}
          minDate={minDate}
          maxDate={maxDate}
          defaultMonth={defaultMonth}
        />
      </Step>

      <Step
        number={2}
        title="Select Entry Time"
        active={Boolean(selectedDate)}
        narrow
        aside={selectedDate ? `— ${longDate(selectedDate)}` : null}
      >
        {!selectedDate ? <p className="sep-hint">Pick a date to see entry times.</p> : null}
        {selectedDate && loading ? <Spinner /> : null}

        {selectedDate && failed ? (
          <Alert color="error" title="We could not load entry times.">
            <Button size="sm" onClick={retry}>Try again</Button>
          </Alert>
        ) : null}

        {selectedDate && !loading && !failed && slots.length === 0 ? (
          <EmptyState title="No entry times" description="Nothing is available on this date." />
        ) : null}

        {selectedDate && !loading && !failed && slots.length > 0 ? (
          <div className="sep-slots">
            {slots.map((slot) => {
              const soldOut = slot.state === 'sold_out';
              const key = slotKey(slot);

              return (
                <button
                  key={key}
                  type="button"
                  className={`sep-slot ${key === activeKey ? 'is-selected' : ''} ${soldOut ? 'is-soldout' : ''}`}
                  disabled={soldOut}
                  aria-pressed={key === activeKey}
                  onClick={() => chooseSlot(slot)}
                >
                  <span className="sep-slot__time">{slot.time_label || 'Tickets'}</span>
                  <span className={`sep-slot__state sep-slot__state--${slot.state}`}>
                    {slot.remaining ? `${slot.remaining} left` : STATE_LABELS[slot.state]}
                  </span>
                </button>
              );
            })}
          </div>
        ) : null}
      </Step>

      <Step number={3} title="Select Tickets" active={Boolean(selectedSlot)}>
        {!selectedSlot ? (
          <p className="sep-hint sep-hint--boxed">Pick a date and time to see available tickets.</p>
        ) : null}

        {selectedSlot ? (
          <div className="sep-checkout">
            <div className="sep-checkout__bar">
              <span>
                <strong>{longDate(selectedDate)}</strong>
                {selectedSlot.time_label ? <> at <strong>{selectedSlot.time_label}</strong></> : null}
              </span>
            </div>

            {checkout.loading ? <div className="sep-checkout__body"><Spinner /></div> : null}

            {checkout.failed ? (
              <div className="sep-checkout__body">
                <Alert color="error" title="We could not load tickets for this time.">
                  <Button size="sm" onClick={() => loadCheckout(selectedSlot)}>Try again</Button>
                </Alert>
              </div>
            ) : null}

            {checkout.props ? (
              <div className="sep-checkout__body">
                <WebOrderFormApp
                  key={checkoutKey}
                  {...checkout.props}
                  onRestart={restartCheckout}
                  onTicketReservationChange={(token, isPlacingOrder) => {
                    placingOrder.current = isPlacingOrder;
                    held.current = token
                      ? { token, confirmId: selectedSlot.confirm_id, csrfToken: checkout.props.csrfToken }
                      : null;
                  }}
                />
              </div>
            ) : null}
          </div>
        ) : null}
      </Step>

      <style>{`
        .sep {
          --sep-ink: #1a1a1a;
          --sep-muted: #6b7280;
          --sep-line: #e5e7eb;
          --sep-line-strong: #d1d5db;
          --sep-surface: #fff;
          --sep-accent: #1a1a1a;
          --sep-open: #16a34a;
          --sep-warn: #b45309;
          --sep-danger: #b91c1c;
          padding: 24px 16px;
        }
        .sep-step--narrow { max-width: 760px; }
        .sep-step { margin-bottom: 36px; }
        .sep-step:last-of-type { margin-bottom: 0; }
        .sep-step__head { display: flex; align-items: center; gap: 12px; margin-bottom: 16px; flex-wrap: wrap; }
        .sep-step__num {
          width: 28px; height: 28px; border-radius: 50%;
          display: inline-flex; align-items: center; justify-content: center;
          font-size: 14px; font-weight: 700; background: #e5e7eb; color: #9ca3af;
        }
        .sep-step__num.is-active { background: var(--sep-ink); color: #fff; }
        .sep-step__title { font-size: 20px; font-weight: 700; margin: 0; color: #9ca3af; }
        .sep-step__title.is-active { color: var(--sep-ink); }
        .sep-step__aside { color: var(--sep-muted); font-size: 15px; }
        .sep-hint { color: #9ca3af; margin: 0; }
        .sep-hint--boxed {
          border: 1px dashed var(--sep-line-strong); border-radius: 10px;
          padding: 28px 16px; text-align: center;
        }

        .sep-slots { display: flex; flex-wrap: wrap; gap: 10px; }
        .sep-slot {
          min-width: 104px; padding: 9px 14px; cursor: pointer;
          border: 1px solid var(--sep-line-strong); border-radius: 8px;
          background: var(--sep-surface); text-align: center; line-height: 1.3;
        }
        .sep-slot:hover:not(:disabled) { border-color: var(--sep-ink); }
        .sep-slot:focus-visible { outline: 2px solid var(--sep-ink); outline-offset: 1px; }
        .sep-slot.is-selected { border-color: var(--sep-ink); box-shadow: inset 0 0 0 1px var(--sep-ink); }
        .sep-slot.is-soldout { cursor: not-allowed; background: #f9fafb; }
        .sep-slot__time { display: block; font-weight: 700; font-size: 15px; color: var(--sep-ink); }
        .sep-slot.is-soldout .sep-slot__time { color: #9ca3af; }
        .sep-slot__state { display: block; font-size: 12px; margin-top: 2px; color: var(--sep-muted); }
        .sep-slot__state--almost_gone { color: var(--sep-warn); font-weight: 600; }
        .sep-slot__state--sold_out { color: var(--sep-danger); }

        .sep-checkout { border: 1px solid var(--sep-line); border-radius: 10px; overflow: hidden; }
        .sep-checkout__bar {
          display: flex; align-items: center; justify-content: space-between; gap: 12px;
          padding: 12px 16px; background: #f9fafb; border-bottom: 1px solid var(--sep-line);
          font-size: 15px; color: var(--sep-muted);
        }
        .sep-checkout__bar strong { color: var(--sep-ink); }
        .sep-checkout__body { padding: 4px 0; }

        @media (max-width: 480px) {
          .sep-slot { flex: 1 1 calc(50% - 10px); min-width: 0; }
        }
      `}</style>
    </div>
  );
};

SeriesEntryPicker.propTypes = {
  dates: PropTypes.arrayOf(PropTypes.shape({
    date: PropTypes.string.isRequired,
    state: PropTypes.string.isRequired
  })).isRequired,
  selectedDate: PropTypes.string,
  entrySlotsUrl: PropTypes.string.isRequired,
  defaultMonth: PropTypes.string,
  minDate: PropTypes.string,
  maxDate: PropTypes.string,
  realTimeEnabled: PropTypes.bool
};

SeriesEntryPicker.defaultProps = {
  realTimeEnabled: false
};

export default SeriesEntryPicker;
